import { useLocation, useOutletContext, useParams } from "react-router-dom";
import { useEffect } from "react";
import { usePost } from "../hooks/usePost";
import PostNotFound from "../../../components/common/PostNotFound";
import PostContentSkeleton from "../../../components/common/skeleton/PostContentSkeleton";
import VideoEmbed from "../../../components/common/VideoEmbed";
import { BlogReaderOutletContext } from "./BlogReaderLayout";
import { useReadingPreferences } from "../../../context/ReadingPreferencesContext";
import { useLocale } from "../../../context/LocaleContext";
import { ReactionBar } from "../../../components/blog/ReactionBar";
import { CommentsSection } from "../../../components/blog/CommentsSection";
import { ReadNext } from "../../../components/blog/ReadNext";
import { startVisit, endVisit, watchForLeaving } from "../../../services/visitTracker";
import { Icon } from "../../../components/common/Icon";
import { useTrackReading } from "../hooks/useTrackReading";
import { getEntry } from "../../../services/readingProgress";
import { currentSectionId, writeSectionHash } from "../lib/articleSections";
import { useInternalLinkRouting } from "../../../hooks/useInternalLinkRouting";

/**
 * A title is a plain string on some endpoints and a locale map on others.
 * Treating the map as a string silently stored the slug instead, which is
 * what the "Resume reading" card ended up showing.
 */
function plainText(value: unknown, locale: string): string | undefined {
  if (typeof value === "string") return value || undefined;
  if (value && typeof value === "object") {
    const byLocale = value as Record<string, string>;
    return byLocale[locale] ?? byLocale.en ?? Object.values(byLocale)[0];
  }
  return undefined;
}

export default function PostPage() {
  const { setPostSections, blog } = useOutletContext<BlogReaderOutletContext>();
  const { blogSlug, postSlug } = useParams();
  const location = useLocation();
  const { fontSizeValue } = useReadingPreferences();
  const { locale, setAvailableLocales } = useLocale();
  const handleContentClick = useInternalLinkRouting();

  // Fetch post content independently — BlogReaderLayout only holds blog metadata.
  const { post, loading: postLoading, error: postError } = usePost(blogSlug, postSlug);

  // Sync post TOC to the layout's right-sidebar whenever the post changes.
  useEffect(() => {
    if (post?.toc) {
      setPostSections(post.toc);
    } else {
      setPostSections([]);
    }
  }, [post, setPostSections]);

  // Sync available locales to context so Header can render LocaleSwitcher.
  useEffect(() => {
    if (post?.availableLocales) {
      setAvailableLocales(post.availableLocales);
    }
    return () => {
      setAvailableLocales([]);
    };
  }, [post?.availableLocales, setAvailableLocales]);

  // Track visit: start on mount (after the post loads), end on unmount — and
  // also when the tab is hidden or the phone locked, which never unmounts.
  useEffect(() => {
    if (!blogSlug || !postSlug || !post) return;

    startVisit(blogSlug, postSlug, locale);
    const stopWatching = watchForLeaving();

    return () => {
      stopWatching();
      endVisit();
    };
  }, [blogSlug, postSlug, post, locale]);

  // Remember how far the reader got, so home can offer to resume it.
  useTrackReading({
    blogSlug,
    blogTitle: plainText(blog?.title, locale),
    postSlug,
    postTitle: plainText(post?.title, locale),
    ready: Boolean(post && !postLoading),
  });

  // Where to land on arrival. A hash means a specific section was asked for —
  // a bookmark, a shared link, or "Resume reading" pointing at the section the
  // reader had got to — so honour it instead of jumping to the top. The target
  // only exists once the article HTML has been injected, hence the dependency
  // on `post` and the frame wait for layout.
  useEffect(() => {
    if (!post) return;

    const id = decodeURIComponent(location.hash.replace(/^#/, ''));
    const resuming = (location.state as { resume?: boolean } | null)?.resume === true;

    if (!id && !resuming) {
      window.scrollTo(0, 0);
      return;
    }

    const frame = requestAnimationFrame(() => {
      if (id && document.getElementById(id)) {
        document.getElementById(id)!.scrollIntoView({ block: 'start' });
        return;
      }

      // Resuming an entry saved before anchors existed: it has no section to
      // aim at, only a percentage, so put the reader back at that depth. This
      // inverts the measurement in useTrackReading.
      const stored = blogSlug && postSlug ? getEntry(blogSlug, postSlug) : null;
      const article = document.querySelector('article');
      if (!stored?.percent || !article) {
        window.scrollTo(0, 0);
        return;
      }

      const start = (article as HTMLElement).offsetTop;
      const height = (article as HTMLElement).clientHeight;
      const top = start + (stored.percent / 100) * height - window.innerHeight;
      window.scrollTo({ top: Math.max(0, top) });

      // Name where we landed, once the browser has applied the scroll. Without
      // this the reader is put back in the right place but the address bar
      // still says the top of the article, so the position cannot be
      // refreshed, bookmarked or shared — and nothing else names it until
      // they scroll.
      requestAnimationFrame(() => writeSectionHash(currentSectionId()));
    });
    return () => cancelAnimationFrame(frame);
  }, [postSlug, blogSlug, post, location.hash, location.state]);

  // Show inline skeleton while post loads so sidebars remain stable.
  if (postLoading) return <PostContentSkeleton />;

  if (postError || !post) {
    return <PostNotFound wrongSlug={postSlug!} blog={blog} />;
  }

  // Where this post sits in its series — the reader should always know.
  const chapterIndex = blog?.posts?.findIndex((p) => p.slug === postSlug) ?? -1;
  const publishedOn = post.date
    ? new Date(post.date).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <div className="flex-1 min-w-0 w-full">
      {/* Kicker + byline — the article's own <h1> follows, inside the content */}
      <div className="flex flex-col gap-3 mb-6">
        {chapterIndex >= 0 && (
          <span className="line-clamp-2 text-[11px] font-bold uppercase tracking-[0.12em] text-primary sm:text-xs sm:tracking-[0.14em]">
            {typeof blog?.title === 'string' ? blog.title : 'Series'} · Chapter {chapterIndex + 1} of{' '}
            {blog?.posts?.length ?? 0}
          </span>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px] font-semibold text-muted-foreground">
          {publishedOn && <span>{publishedOn}</span>}
          {publishedOn && post.readTime ? (
            <span className="h-1 w-1 rounded-full bg-border" aria-hidden="true" />
          ) : null}
          {post.readTime ? (
            <span className="inline-flex items-center gap-1.5">
              <Icon name="clock" size={14} />
              {post.readTime} min read
            </span>
          ) : null}
        </div>
      </div>

      {/* Cover image — shown when there is no video embed */}
      {post.coverImage && !post.videoEmbedUrl && (
        <img
          src={post.coverImage}
          alt={typeof post.title === 'string' ? post.title : ''}
          className="w-full rounded-xl object-cover max-h-72 mb-6"
        />
      )}

      {post.videoEmbedUrl && (
        <VideoEmbed url={post.videoEmbedUrl} title={post.title} />
      )}

      {/* Prose article — font-size comes from ReadingPreferencesContext.
          min-w-0 + w-full prevent the flex child from expanding the parent.
          overflow-x-hidden clips any pre/table overflow so changing font size
          only affects line wrapping / height, never the container width. */}
      <article
        className="prose max-w-none w-full min-w-0 overflow-x-hidden pb-4 md:pb-0"
        style={{ fontSize: fontSizeValue, transition: 'font-size 200ms ease-in-out' }}
        onClick={handleContentClick}
        dangerouslySetInnerHTML={{ __html: post.htmlContent }}
      />

      {/* Reactions */}
      <div className="mt-6 pt-4 border-t-2 border-surface-variant">
        <ReactionBar blogSlug={blogSlug!} postSlug={postSlug!} />
      </div>

      {/* Where to go next, before the conversation rather than after it: a
          reader who has finished is deciding whether to stay on the site. */}
      <ReadNext blogSlug={blogSlug} postSlug={postSlug} />

      {/* Comments */}
      <CommentsSection blogSlug={blogSlug!} postSlug={postSlug!} />
    </div>
  );
}